// frontend/user/src/_lib/use-draggable-fab.ts
// 可拖动悬浮按钮(FAB)共享 hook。
// 用于 forum 写文章 FAB、files/folders 新建 FAB。
// 默认位置:离底部 188px、离右边 40px;指针拖动后改用 viewport 坐标自由摆放。
//
// 设计要点:
// - 用 Pointer Events(同时覆盖触摸 / 鼠标),拖动中 touchAction:'none' 防页面滚动。
// - movedRef 记录本次手势是否真的移动过;onClick 经 guardClick 包裹,拖动后不触发
//   导航 / 弹菜单,纯点击照常触发。
// - 位置 clamp 在 viewport 内,避免拖出屏幕够不到。
// - 不持久化:每次进入页面回到默认位置(本期需求只要「可随意拖动」)。
import { useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from 'react'

/** FAB 默认锚点(px):离右边 / 离底部。 */
export const FAB_DEFAULT_RIGHT = 40
export const FAB_DEFAULT_BOTTOM = 188

/** FAB 尺寸(h-14 w-14 = 56px),用于 clamp 边界。 */
const FAB_SIZE = 56
/** 视为「拖动」的最小位移阈值(px),低于此值算点击。 */
const DRAG_THRESHOLD = 4
/** 距 viewport 边缘的最小留白(px)。 */
const EDGE_GAP = 8

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v))
}

type Anchor = { right: number; bottom: number }

export function useDraggableFab(opts?: { right?: number; bottom?: number }) {
  const right0 = opts?.right ?? FAB_DEFAULT_RIGHT
  const bottom0 = opts?.bottom ?? FAB_DEFAULT_BOTTOM
  const [pos, setPos] = useState<Anchor>({ right: right0, bottom: bottom0 })
  const drag = useRef<{ sx: number; sy: number; or: number; ob: number } | null>(null)
  const movedRef = useRef(false)

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    drag.current = { sx: e.clientX, sy: e.clientY, or: pos.right, ob: pos.bottom }
    movedRef.current = false
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current
    if (!d) return
    const dx = e.clientX - d.sx
    const dy = e.clientY - d.sy
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) movedRef.current = true
    // right / bottom 锚点:光标右移 → right 减小;光标下移 → bottom 减小。
    const maxRight = window.innerWidth - FAB_SIZE - EDGE_GAP
    const maxBottom = window.innerHeight - FAB_SIZE - EDGE_GAP
    setPos({
      right: clamp(d.or - dx, EDGE_GAP, maxRight),
      bottom: clamp(d.ob - dy, EDGE_GAP, maxBottom),
    })
  }

  const onPointerUp = (e: ReactPointerEvent<HTMLElement>) => {
    drag.current = null
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }

  /** 包裹原 onClick:本次手势若发生拖动则吞掉点击,纯点击照常触发。 */
  const guardClick =
    (fn: () => void) => (e: ReactMouseEvent<HTMLElement>) => {
      if (movedRef.current) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      fn()
    }

  const dragProps = {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
    style: {
      right: pos.right,
      bottom: pos.bottom,
      touchAction: 'none' as const,
    },
  }

  return { dragProps, guardClick }
}
